import React, { useState } from 'react';
import './Onboarding.css';

interface OnboardingProps {
    onComplete: () => void;
}

const slides = [
    {
        emoji: '🐾',
        title: 'Welcome to Foster Pal',
        description: 'Track feedings, weights, and care for your foster animals. Built for exhausted foster parents at 3am.',
        image: null,
    },
    {
        emoji: '🐱',
        title: 'Try It Out First',
        description: 'We\'ve added a sample foster named Whiskers so you can explore the app. Delete them anytime to start tracking your real fosters.',
        image: 'feature-sample',
    },
    {
        emoji: '🥛',
        title: 'Two Taps to Feed',
        description: 'Log a feeding in seconds. "Same as Last" means you can track care even when you\'re half asleep.',
        image: 'feature-feed',
    },
    {
        emoji: '⚖️',
        title: 'Smart Weight Alerts',
        description: 'Get warned immediately if your foster isn\'t gaining weight. Catch health issues before they become critical.',
        image: 'feature-weight',
    },
    {
        emoji: '💩',
        title: 'Track Everything',
        description: 'Elimination tracking with consistency and color. All the details your coordinator needs to know.',
        image: 'feature-poop',
    },
    {
        emoji: '📱',
        title: 'Works Offline',
        description: 'All your data stays on your device. No internet required. Install it like an app and use it anywhere.',
        image: 'feature-offline',
    },
];

export function Onboarding({ onComplete }: OnboardingProps) {
    const [currentSlide, setCurrentSlide] = useState(0);

    const nextSlide = () => {
        if (currentSlide < slides.length - 1) {
            setCurrentSlide(currentSlide + 1);
        } else {
            onComplete();
        }
    };

    const prevSlide = () => {
        if (currentSlide > 0) {
            setCurrentSlide(currentSlide - 1);
        }
    };

    const skip = () => {
        onComplete();
    };

    const slide = slides[currentSlide];
    const isLastSlide = currentSlide === slides.length - 1;

    return (
        <div className="onboarding-overlay">
            <div className="onboarding-container">
                <button className="onboarding-skip" onClick={skip}>
                    Skip
                </button>

                <div className="onboarding-content">
                    <div className="onboarding-emoji">{slide.emoji}</div>
                    <h1 className="onboarding-title">{slide.title}</h1>
                    <p className="onboarding-description">{slide.description}</p>

                    {slide.image && (
                        <div className="onboarding-image-placeholder">
                            <div className="placeholder-icon">
                                {slide.image === 'feature-sample' && '🐱'}
                                {slide.image === 'feature-feed' && '🥛'}
                                {slide.image === 'feature-weight' && '⚖️'}
                                {slide.image === 'feature-poop' && '💩'}
                                {slide.image === 'feature-offline' && '📱'}
                            </div>
                        </div>
                    )}
                </div>

                <div className="onboarding-footer">
                    <div className="onboarding-dots">
                        {slides.map((_, index) => (
                            <div
                                key={index}
                                className={`onboarding-dot ${index === currentSlide ? 'active' : ''}`}
                                onClick={() => setCurrentSlide(index)}
                            />
                        ))}
                    </div>

                    <div className="onboarding-buttons">
                        {currentSlide > 0 && (
                            <button className="onboarding-btn secondary" onClick={prevSlide}>
                                Back
                            </button>
                        )}
                        <button
                            className="onboarding-btn primary"
                            onClick={nextSlide}
                            style={currentSlide === 0 ? { marginLeft: 'auto' } : {}}
                        >
                            {isLastSlide ? 'Get Started' : 'Next'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}